import type { AuditScoreResult } from '@/types/gmb';
import {
  buildDiagnosisDimensions,
  type DiagnosisDimension,
} from '@/pages/gmb-audit/utils/diagnosisDimensions';

// UI-2A｜4 大診斷構面（Horizontal Progress Dashboard）。
// 只呈現既有 11 Metrics 的 grouping；不重新評分、不建立第二套 100 分、不使用 Radar。

/** 依完成率給一句簡短狀態（純文案，不改分數）。 */
function qualitativeStatus(rate: number): string {
  if (rate >= 0.8) return '大致完整';
  if (rate >= 0.6) return '仍算穩定';
  if (rate >= 0.4) return '仍有缺口';
  return '明顯不足';
}

/** 進度條顏色（與 11 項明細一致，避免「錯誤頁」感的紅色）。 */
function barColor(rate: number): string {
  if (rate >= 0.7) return 'bg-primary-600';
  if (rate >= 0.4) return 'bg-accent-500';
  return 'bg-accent-600';
}

function DimensionCard({ dimension }: { dimension: DiagnosisDimension }) {
  const hasData = dimension.availableMax > 0;
  const ratePct = hasData ? Math.round(dimension.completionRate * 100) : 0;

  let statusText = '資料不足';
  if (hasData) {
    if (dimension.hasInsufficientPeerData) statusText = '同業資料不足';
    else if (dimension.hasInsufficientData) statusText = '部分資料不足';
    else statusText = qualitativeStatus(dimension.completionRate);
  }

  return (
    <div className="rounded-2xl border border-background-300 bg-background-50 p-5 shadow-card md:p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg text-foreground-950">{dimension.title}</h3>
          <p className="mt-1 text-sm leading-relaxed text-foreground-500">{dimension.caption}</p>
        </div>
        <span className="ds-badge shrink-0 bg-secondary-100 text-secondary-900">{statusText}</span>
      </div>

      {hasData ? (
        <>
          <div className="mt-5 flex items-end justify-between gap-3">
            <span className="text-3xl font-bold leading-none text-foreground-950">
              {dimension.earned}
              <span className="text-base font-medium text-foreground-400"> / {dimension.availableMax}</span>
            </span>
            <span className="text-lg font-bold text-primary-800">{ratePct}%</span>
          </div>
          <div
            className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-background-200"
            role="progressbar"
            aria-valuenow={ratePct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${dimension.title} 完成率 ${ratePct}%`}
          >
            <div
              className={`h-full rounded-full ${barColor(dimension.completionRate)}`}
              style={{ width: `${ratePct}%` }}
            />
          </div>
        </>
      ) : (
        <div className="mt-5 flex items-center gap-3 rounded-xl border border-dashed border-background-400 bg-background-100 px-4 py-3.5 text-base text-foreground-600">
          <i className="ri-information-line text-lg text-foreground-400" />
          此構面資料不足，暫無法計算完成率。
        </div>
      )}
    </div>
  );
}

export default function ReportDimensionsSection({ result }: { result: AuditScoreResult }) {
  const dimensions = buildDiagnosisDimensions(result.metrics);

  return (
    <section className="ds-container pt-12 md:pt-16">
      <div className="mb-3 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
          <i className="ri-layout-grid-line text-xl" />
        </span>
        <h2 className="text-xl md:text-2xl">4 大診斷構面</h2>
      </div>
      <p className="mb-6 text-base text-foreground-500">將 11 項指標歸類為 4 大構面，快速看出經營體質分布。</p>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {dimensions.map((dimension) => (
          <DimensionCard key={dimension.key} dimension={dimension} />
        ))}
      </div>
    </section>
  );
}